import toast from "react-hot-toast";
import axiosInstance from "helpers/api";
import { formatErrorForReact } from "helpers/utils";
import Logo from "components/common/Logo";
import { useCookies } from "react-cookie";
import Button from "components/base/Button";
import { loadStripe } from "@stripe/stripe-js";
import { Fragment, useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

type Inputs = {
  phone: string;
  email: string;
  password: string;
  last_name: string;
  first_name: string;
  confirm_password: string;
};

const SignUpPaidForm = () => {
  const [cookie, setCookie] = useCookies(["isLoggedInKIQ", "stripeIdpKey"]);
  const stripePromise = loadStripe(
    process.env.REACT_STRIPE_PUB_KEY as string
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const interval = searchParams.get("interval");
  const passwordValue = watch("password");

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setIsLoading(true);
    try {
      let idempotency_key = cookie?.stripeIdpKey;
    
      if (!idempotency_key) {
        idempotency_key = crypto?.randomUUID();
        setCookie('stripeIdpKey', idempotency_key, {
          path: '/',
          maxAge: 86400 // 24 hours
        });
      }

      const response = await axiosInstance.post("/auth/paid-registration", {
        plan_name: type,
        idempotency_key,
        email: data.email,
        phone: data.phone,
        interval: interval,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        confirm_password: data.confirm_password,
      });

      const stripe = await stripePromise;

      const result = await stripe?.redirectToCheckout({
        sessionId: response.data.id,
      });
      if (result?.error) {
        console.log(result.error);
      }
    } catch (error: unknown) {
      let errorMessage = "Registration failed";
      if (error && typeof error === 'object' && 'response' in error && error.response && typeof error.response === 'object' && 'data' in error.response && error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        errorMessage = String(error.response.data.message);
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(formatErrorForReact(errorMessage));
    }
    setIsLoading(false);
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (cookie.isLoggedInKIQ) {
      navigate("/dashboard");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookie.isLoggedInKIQ]);

  return (
    <Fragment>
      <div className="text-center mb-4 mb-lg-7">
        <Link to="/" className="d-flex flex-center text-decoration-none mb-4">
          <Logo
            text={false}
            width={100}
            className="fw-bolder fs-5 d-inline-block"
          />
        </Link>
        <h3 className="text-body-highlight">{type} Onboarding Details</h3>
        <p className="text-body-tertiary">Get access to your account</p>
      </div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Form.Group as={Col} lg={6} className="mb-2 text-start">
            <Form.Label htmlFor="first_name">
              First Name
              <span className="text-danger"> *</span>
            </Form.Label>
            <Form.Control
              id="first_name"
              type="text"
              placeholder="First Name"
              {...register("first_name", {
                required: true,
                maxLength: 200,
              })}
            />
            {errors.first_name?.type === "required" && (
              <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                First name is required
              </p>
            )}
          </Form.Group>
          <Form.Group as={Col} lg={6} className="mb-2 text-start">
            <Form.Label htmlFor="last_name">Last Name</Form.Label>
            <Form.Control
              id="last_name"
              type="text"
              placeholder="Last Name"
              {...register("last_name", {
                maxLength: 200,
              })}
            />
          </Form.Group>
        </Row>
        <Row>
          <Col lg={6}>
            <Form.Group className="mb-2 text-start">
              <Form.Label htmlFor="phone">
                Phone Number
                <span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                id="phone"
                type="tel"
                inputMode="tel"
                placeholder="Phone Number"
                {...register("phone", { required: true, maxLength: 100 })}
              />
              {errors.phone?.type === "required" && (
                <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                  Phone number is required
                </p>
              )}
            </Form.Group>
          </Col>
          <Col lg={6}>
            <Form.Group className="mb-2 text-start">
              <Form.Label htmlFor="email">
                Email
                <span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                id="email"
                type="email"
                placeholder="Email"
                {...register("email", { required: true, maxLength: 100 })}
              />
              {errors.email?.type === "required" && (
                <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                  Email is required
                </p>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col lg={6}>
            <Form.Group className="position-relative">
              <Form.Label htmlFor="password">
                Password<span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                className="pe-5"
                autoComplete="new-password"
                {...register("password", {
                  required: true,
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters long.",
                  },
                  maxLength: {
                    value: 40,
                    message: "Password cannot exceed 40 characters.",
                  },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="btn btn-link p-0 position-absolute end-0 translate-middle-y me-2"
                style={{ textDecoration: "none", top: "72%" }}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </Form.Group>
            {errors.password?.type === "required" && (
              <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                Password is required
              </p>
            )}
            {errors.password && errors.password.message && (
              <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                {errors.password.message}
              </p>
            )}
          </Col>

          <Col lg={6}>
            <Form.Group className="position-relative">
              <Form.Label htmlFor="confirmPassword">
                Confirm Password<span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                className="pe-5"
                autoComplete="new-password"
                {...register("confirm_password", {
                  required: "Confirm password is required",
                  validate: (value) =>
                    value === passwordValue || "Passwords do not match",
                })}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="btn btn-link p-0 position-absolute end-0 translate-middle-y me-2"
                style={{ textDecoration: "none", top: "72%" }}
              >
                <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
              </button>
            </Form.Group>
            {errors.confirm_password && (
              <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                {errors.confirm_password.message}
              </p>
            )}
          </Col>
        </Row>

        <Button
          disabled={isLoading}
          variant="primary"
          type="submit"
          className="w-100 mt-4"
        >
          {isLoading ? (
            <Spinner animation="border" role="status" size="sm" />
          ) : (
            "Submit"
          )}
        </Button>
      </Form>
    </Fragment>
  );
};

export default SignUpPaidForm;
